import { useNavigate } from 'react-router-dom';
import type { MyShift } from '@keetcheck/shared';
import { theme } from '../theme.js';
import { useAuth, useAccess } from '../context/AuthContext.js';
import {
  useMyShifts, useCoverRequests, useSwaps, useScratchWindows, useActivities, useCheckRuns,
  useDashboard, useOrgMembers, useOrgInvites, useShifts, useFaults,
} from '../lib/queries.js';
import { PageShell } from '../components/PageShell.js';
import { Icon } from '../components/Icon.js';
import { InstallCard } from '../components/InstallCard.js';
import { UnitSwitcher } from '../components/UnitSwitcher.js';
import { OnNowCard } from '../components/OnNowCard.js';
import { TeamChip } from '../components/RosterGrid.js';
import { leaveLabel } from '../components/LeaveCard.js';
import { AS_LABEL, KIND_LABEL, dayLabel, timeRange, currentDutyMonday, addDays, shortDate } from '../lib/shifts.js';

/**
 * Home: a one-screen summary. Each card taps through to the detail —
 * what needs me, my next shift, who's on, this week, checks, and (admins
 * and team leaders) the unit.
 */
export function DashboardPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const orgId = user?.orgId ?? '';
  const { me, isAdmin, ledTeam } = useAccess();
  const name = me?.displayName ?? '';

  return (
    <PageShell>
      <div style={{
        padding: '14px 18px 12px', background: theme.surface, borderBottom: `1px solid ${theme.border}`,
        display: 'flex', alignItems: 'center', gap: 10, position: 'sticky', top: 0, zIndex: 5, flexShrink: 0,
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <UnitSwitcher />
        </div>
        <button onClick={() => nav('/search')} aria-label="Search" style={roundBtn}>
          <Icon name="search" size={18} />
        </button>
        <button onClick={() => nav('/help')} aria-label="Help" style={roundBtn}>
          <Icon name="help" size={19} />
        </button>
        <button onClick={() => nav('/profile')} aria-label="Profile" style={{ ...roundBtn, fontWeight: 700, fontSize: 14 }}>
          {name.slice(0, 2).toUpperCase()}
        </button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', background: theme.bg, padding: '4px 0 24px', display: 'flex', flexDirection: 'column' }}>
        <InstallCard dismissible />
        <NeedsYou orgId={orgId} meId={me?.id} canManage={isAdmin || !!ledTeam} />
        <NextShift meId={me?.id} orgId={orgId} />
        <OnNowCard orgId={orgId} />
        <ThisWeek orgId={orgId} />
        <ChecksSummary orgId={orgId} isAdmin={isAdmin} />
        {(isAdmin || ledTeam) && <UnitSummary orgId={orgId} isAdmin={isAdmin} teamId={ledTeam?.id ?? null} />}
      </div>
    </PageShell>
  );
}

// ── Needs you ─────────────────────────────────────────────────

/** Things waiting on me, one line each. Hidden when there's nothing. */
function NeedsYou({ orgId, meId, canManage }: { orgId: string; meId: string | undefined; canManage: boolean }) {
  const nav = useNavigate();
  const { data: open = [] } = useCoverRequests(orgId, 'open');
  const { data: mine = [] } = useCoverRequests(orgId, 'mine');
  const { data: swaps = [] } = useSwaps(orgId);
  const { data: windows = [] } = useScratchWindows(orgId);
  const { data: acts = [] } = useActivities(orgId);
  const { data: shifts = [] } = useMyShifts();

  const now = Date.now();
  const canTake = open.filter((r) => r.canTake).length;
  const toAnswer = swaps.filter((x) => x.role === 'target' && x.status === 'pending').length;
  const myOpen = mine.filter((r) => r.mine && !r.askedBy && r.status === 'open' && r.gaps.length > 0).length;
  const forOthers = canManage ? mine.filter((r) => r.askedBy?.userId === meId && r.status === 'open' && r.gaps.length > 0).length : 0;
  // My team's open ops-support slots, once the Thursday 6pm cut-off is near.
  const cutoff = (ps: string) => new Date(`${addDays(ps, -4)}T18:00:00`).getTime();
  const opsWeeks = [...new Set(shifts.filter((s) => s.as === 'ops_team').map((s) => s.periodStart))]
    .filter((ps) => cutoff(ps) > now && cutoff(ps) - now < 8 * 86400_000);
  const scratch = windows.filter((x) => !x.myReplied && !x.publishedAt && new Date(x.replyBy).getTime() > now);
  const reply = acts.filter((a) => !a.cancelled && a.responseMode !== 'none' && !a.myResponse
    && new Date(a.startsAt).getTime() < now + 21 * 86400_000);

  const rows: { key: string; text: string; to: string; tone?: 'warn' }[] = [];
  if (myOpen) rows.push({ key: 'mine', text: `Your cover request${myOpen > 1 ? 's' : ''} still need${myOpen > 1 ? '' : 's'} someone`, to: '/shifts?tab=cover', tone: 'warn' });
  if (canTake) rows.push({ key: 'take', text: `${canTake} cover request${canTake > 1 ? 's' : ''} you could take`, to: '/shifts?tab=cover' });
  if (toAnswer) rows.push({ key: 'swap', text: `${toAnswer} swap offer${toAnswer > 1 ? 's' : ''} to answer`, to: '/shifts?tab=cover' });
  for (const ps of opsWeeks) {
    const n = shifts.filter((s) => s.as === 'ops_team' && s.periodStart === ps).length;
    rows.push({ key: `ops-${ps}`, text: `Ops support, week of ${shortDate(ps)}: ${n} shift${n > 1 ? 's' : ''} to pick by ${dayLabel(new Date(cutoff(ps)).toISOString())} 6pm`, to: `/ops?week=${ps}` });
  }
  for (const w of scratch) rows.push({ key: `scr-${w.id}`, text: `${w.title}: give your availability`, to: `/scratch/${w.id}` });
  if (reply.length) rows.push({ key: 'act', text: reply.length === 1 ? `Reply to ${reply[0]!.title}` : `${reply.length} activities to reply to`, to: reply.length === 1 ? `/activity/${reply[0]!.id}` : '/shifts?tab=activities' });
  if (forOthers) rows.push({ key: 'others', text: `${forOthers} cover request${forOthers > 1 ? 's' : ''} you asked for members — still open`, to: '/shifts?tab=cover', tone: 'warn' });

  if (!rows.length) return null;
  return (
    <Card title="Needs you" accent>
      {rows.map((r) => (
        <Row key={r.key} onClick={() => nav(r.to)}>
          <span style={{ color: r.tone === 'warn' ? theme.status.warn.fg : theme.text, fontWeight: 600 }}>{r.text}</span>
        </Row>
      ))}
    </Card>
  );
}

// ── Your next shift ───────────────────────────────────────────

function NextShift({ orgId, meId }: { orgId: string; meId: string | undefined }) {
  const nav = useNavigate();
  const { data: shifts = [], isLoading } = useMyShifts();
  const { data: members = [] } = useOrgMembers(orgId);
  const leave = members.find((m) => m.userId === meId)?.leave;
  const now = Date.now();
  const upcoming = shifts.filter((s) => s.as !== 'ops_team' && !s.onLeave && new Date(s.endsAt).getTime() > now);
  if (isLoading) return null;
  const next = upcoming.slice(0, 2);
  return (
    <Card title="Your next shift" onAll={() => nav('/shifts')}>
      {leave?.active && <div style={{ fontSize: 13, color: theme.status.warn.fg, fontWeight: 600, padding: '2px 0 6px' }}>{leaveLabel(leave)}</div>}
      {!next.length && <div style={muted}>Nothing rostered in the coming weeks.</div>}
      {next.map((s) => <ShiftLine key={s.id} s={s} />)}
    </Card>
  );
}

function ShiftLine({ s }: { s: MyShift }) {
  const nav = useNavigate();
  const started = new Date(s.startsAt).getTime() <= Date.now();
  const what = s.as === 'activity' ? s.activity!.title
    : s.as === 'cover' ? `Covering ${s.coverFor?.displayName ?? ''} · ${KIND_LABEL[s.kind]}`
    : AS_LABEL[s.as];
  const canAsk = !started && !s.myCover && s.as !== 'cover' && s.as !== 'activity';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', borderTop: `1px solid ${theme.border}` }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: theme.text }}>{started ? 'Now' : dayLabel(s.startsAt)}</span>
          <span style={{ fontSize: 14, color: theme.text }}>· {what}</span>
          {s.as === 'duty_crew' && <TeamChip name={s.teamName} />}
        </div>
        <div style={{ fontSize: 12, color: theme.textMuted }}>
          {timeRange(s.startsAt, s.endsAt)}{s.holiday ? ` · ${s.holiday}` : ''}
          {s.myCover && <span style={{ color: s.myCover.gaps.length ? theme.status.warn.fg : theme.status.ok.fg, fontWeight: 600 }}>
            {' · '}{s.myCover.gaps.length ? 'cover asked — gap left' : 'covered'}</span>}
        </div>
      </div>
      {canAsk && (
        <button onClick={() => nav(`/cover/new?shift=${encodeURIComponent(s.id)}`)} style={smallBtn}>Can't make it</button>
      )}
    </div>
  );
}

// ── This week ─────────────────────────────────────────────────

function ThisWeek({ orgId }: { orgId: string }) {
  const nav = useNavigate();
  const ps = currentDutyMonday();
  const { data: shifts = [], isLoading } = useShifts(orgId, ps, addDays(ps, 7));
  const { data: acts = [] } = useActivities(orgId);
  if (isLoading || !shifts.length) return null;
  const now = Date.now();
  const week = shifts.filter((s) => s.periodStart === ps);
  const duty = week.find((s) => s.kind === 'duty_crew')?.teamName ?? null;
  const ops = week.find((s) => s.kind === 'ops_support')?.teamName ?? null;
  const udo = week.find((s) => s.kind === 'udo' && s.userName)?.userName ?? null;
  const short = week.filter((s) => s.kind === 'duty_crew' && new Date(s.endsAt).getTime() > now && s.capability?.warnings.length)
    .slice(0, 2);
  const end = new Date(`${addDays(ps, 7)}T18:00:00`).getTime();
  const events = acts.filter((a) => !a.cancelled && new Date(a.startsAt).getTime() > now && new Date(a.startsAt).getTime() < end).slice(0, 2);
  return (
    <Card title={`This week · from ${shortDate(ps)}`} onAll={() => nav('/shifts?tab=week')}>
      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px', fontSize: 13, alignItems: 'center' }}>
        <span style={label}>Duty crew</span><span>{duty ? <TeamChip name={duty} /> : <span style={muted}>Scratch crews</span>}</span>
        <span style={label}>Ops support</span><span>{ops ? <TeamChip name={ops} /> : <span style={muted}>—</span>}</span>
        <span style={label}>UDO</span><span style={{ color: udo ? theme.text : theme.status.warn.fg, fontWeight: udo ? 500 : 600 }}>{udo ?? 'Not picked'}</span>
      </div>
      {short.map((s) => (
        <div key={s.id} style={{ fontSize: 12, fontWeight: 600, color: theme.status.danger.fg, marginTop: 6 }}>
          {dayLabel(s.startsAt)} {timeRange(s.startsAt, s.endsAt)}: {s.capability!.warnings[0]}
        </div>
      ))}
      {events.map((a) => (
        <div key={a.id} style={{ fontSize: 12, color: theme.textDim, marginTop: 6 }}>
          {dayLabel(a.startsAt)} · {a.title}{a.hostTeam ? ` · ${a.hostTeam.name} hosting` : ''}
        </div>
      ))}
    </Card>
  );
}

// ── Checks ────────────────────────────────────────────────────

function ChecksSummary({ orgId, isAdmin }: { orgId: string; isAdmin: boolean }) {
  const nav = useNavigate();
  // Non-admins' runs are already limited to their own team by the API.
  const { data: runs = [] } = useCheckRuns(orgId, { status: 'draft' });
  const { data: dash } = useDashboard(orgId, isAdmin);
  const { data: reg } = useFaults(orgId);
  const faults = reg?.faults.length ?? 0;
  const offRun = reg?.offRun.length ?? 0;
  const parts = [
    `${runs.length} open`,
    dash && dash.overdueRuns.length ? `${dash.overdueRuns.length} overdue` : null,
  ].filter(Boolean);
  const faultParts = [
    `${faults} open fault${faults === 1 ? '' : 's'}`,
    offRun ? `${offRun} off run` : null,
  ].filter(Boolean);
  return (
    <Card title="Checks & faults" onAll={() => nav('/checks')}>
      <Row onClick={() => nav('/checks')}>
        <span style={{ fontWeight: 600, color: dash?.overdueRuns.length ? theme.status.danger.fg : theme.text }}>Checks: {parts.join(' · ')}</span>
      </Row>
      <Row onClick={() => nav('/faults')}>
        <span style={{ fontWeight: 600, color: offRun ? theme.status.danger.fg : faults ? theme.status.warn.fg : theme.text }}>{faultParts.join(' · ')}</span>
      </Row>
    </Card>
  );
}

// ── Unit (admins, team leaders) ───────────────────────────────

function UnitSummary({ orgId, isAdmin, teamId }: { orgId: string; isAdmin: boolean; teamId: string | null }) {
  const nav = useNavigate();
  const { data: open = [] } = useCoverRequests(orgId, 'open');
  const { data: members = [] } = useOrgMembers(orgId);
  const { data: invites = [] } = useOrgInvites(isAdmin ? orgId : '');
  const { data: dash } = useDashboard(orgId, isAdmin);
  const now = Date.now();
  const gaps = open.filter((r) => r.gaps.length > 0 && new Date(r.startsAt).getTime() < now + 7 * 86400_000).length;
  const scope = isAdmin ? members : members.filter((m) => m.teamId === teamId);
  const onLeave = scope.filter((m) => m.leave?.active).length;
  const due = dash ? dash.expiring.length + dash.serviceDue.length : 0;
  const peopleTo = isAdmin ? '/admin/members' : '/admin/teams';

  const rows: { key: string; n: number; text: string; to: string; tone: 'danger' | 'warn' | 'muted' }[] = [
    { key: 'gaps', n: gaps, text: `cover gap${gaps === 1 ? '' : 's'} in the next 7 days`, to: '/shifts?tab=cover', tone: 'danger' },
    { key: 'leave', n: onLeave, text: `${isAdmin ? 'member' : 'team member'}${onLeave === 1 ? '' : 's'} on leave`, to: peopleTo, tone: 'warn' },
  ];
  if (isAdmin) {
    rows.push({ key: 'inv', n: invites.length, text: `invite${invites.length === 1 ? '' : 's'} waiting`, to: '/admin/members', tone: 'muted' });
    rows.push({ key: 'due', n: due, text: 'equipment due soon', to: '/checks', tone: 'warn' });
  }
  return (
    <Card title={isAdmin ? 'Unit' : 'Your team'}>
      {rows.map((r) => (
        <Row key={r.key} onClick={() => nav(r.to)}>
          <span style={{
            minWidth: 26, textAlign: 'center', fontWeight: 800, fontVariantNumeric: 'tabular-nums',
            color: r.n === 0 ? theme.textMuted : r.tone === 'muted' ? theme.text : theme.status[r.tone].fg,
          }}>{r.n}</span>
          <span style={{ color: r.n ? theme.text : theme.textMuted }}>{r.text}</span>
        </Row>
      ))}
    </Card>
  );
}

// ── Layout bits ───────────────────────────────────────────────

function Card({ title, children, onAll, accent = false }: { title: string; children: React.ReactNode; onAll?: () => void; accent?: boolean }) {
  return (
    <div style={{
      margin: '12px 16px 0', padding: '10px 14px 8px', borderRadius: 12, background: theme.surface, flexShrink: 0,
      border: `1px solid ${accent ? theme.accent : theme.border}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
        <span style={{ flex: 1, fontSize: 12, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase', color: accent ? theme.accent : theme.textMuted }}>{title}</span>
        {onAll && (
          <button onClick={onAll} aria-label={`Open ${title}`} style={{ background: 'none', border: 'none', padding: 2, cursor: 'pointer', color: theme.textMuted, display: 'flex' }}>
            <Icon name="chevron" size={14} />
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

function Row({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  return (
    <button onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 0', background: 'none', border: 'none',
      borderTop: `1px solid ${theme.border}`, textAlign: 'left', cursor: 'pointer', fontFamily: theme.font, fontSize: 13,
    }}>
      <span style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 8 }}>{children}</span>
      <Icon name="chevron" size={14} style={{ color: theme.textMuted, flexShrink: 0 }} />
    </button>
  );
}

const muted: React.CSSProperties = { fontSize: 13, color: theme.textMuted };
const label: React.CSSProperties = { fontSize: 12, fontWeight: 700, color: theme.textMuted };
const roundBtn: React.CSSProperties = {
  width: 38, height: 38, borderRadius: 19, background: theme.surface2, border: `1px solid ${theme.border}`,
  display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: theme.text, flexShrink: 0,
};
const smallBtn: React.CSSProperties = {
  padding: '6px 10px', borderRadius: 8, border: `1px solid ${theme.border}`, background: theme.surface,
  color: theme.text, fontFamily: theme.font, fontSize: 12, fontWeight: 600, cursor: 'pointer', flexShrink: 0,
};
